import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LocalShipping from '@mui/icons-material/esm/LocalShipping';
import Refresh from '@mui/icons-material/esm/Refresh';
import Send from '@mui/icons-material/esm/Send';
import { RootState } from '../../store';
import dailyDispatchService, {
  DailyDispatch,
  DailyDispatchPacket,
  DispatchDeliveryChannel,
} from '../../services/api/dailyDispatchService';
import teamService, { TeamListItem } from '../../services/api/teamService';
import { Task } from '../../services/api/tasksService';
import { isDispatchSendable, toLocalIsoDate } from './supervisorFieldUtils';

const taskLocation = (task: Task) => task.workLocationRef?.name || task.workLocation || task.component?.unit?.code || 'Site';

export default function SupervisorDispatchPage() {
  const { activeProjectId, projects, loading: projectsLoading } = useSelector((state: RootState) => state.projects);
  const effectiveProjectId = activeProjectId ?? projects[0]?.id ?? null;
  const [dispatchDate, setDispatchDate] = useState(toLocalIsoDate(new Date()));
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [teamId, setTeamId] = useState('');
  const [eligibleTasks, setEligibleTasks] = useState<Task[]>([]);
  const [dispatches, setDispatches] = useState<DailyDispatch[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [channel, setChannel] = useState<DispatchDeliveryChannel>('both');
  const [notes, setNotes] = useState('');
  const [activePacket, setActivePacket] = useState<DailyDispatchPacket | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const projectName = projects.find((project) => project.id === effectiveProjectId)?.name ?? 'Active project';
  const selectedTeam = teams.find((team) => team.id === teamId);

  const loadTeams = useCallback(async () => {
    if (!effectiveProjectId) return;
    try {
      const rows = (await teamService.getTeams())
        .filter((team) => team.isActive !== false)
        .sort((a, b) => {
          const aField = /crew|construction|field/i.test(`${a.name} ${a.teamType}`) ? 0 : 1;
          const bField = /crew|construction|field/i.test(`${b.name} ${b.teamType}`) ? 0 : 1;
          return aField - bField || a.name.localeCompare(b.name);
        });
      setTeams(rows);
      setTeamId((current) => current || rows[0]?.id || '');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load teams for dispatch.');
    }
  }, [effectiveProjectId]);

  const loadDispatchWork = useCallback(async () => {
    if (!effectiveProjectId || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const [tasks, rows] = await Promise.all([
        dailyDispatchService.getEligibleTasks({ projectId: effectiveProjectId, dispatchDate, teamId, limit: 100 }),
        dailyDispatchService.listDispatches({ projectId: effectiveProjectId, dispatchDate, teamId }),
      ]);
      setEligibleTasks(tasks);
      setDispatches(rows);
      setSelectedTaskIds(new Set(tasks.map((task) => task.id)));
      setActivePacket(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load dispatch work.');
    } finally {
      setLoading(false);
    }
  }, [effectiveProjectId, dispatchDate, teamId]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    loadDispatchWork();
  }, [loadDispatchWork]);

  const allSelected = eligibleTasks.length > 0 && selectedTaskIds.size === eligibleTasks.length;
  const toggleTask = (taskId: string) => {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const generatePacket = async () => {
    if (!effectiveProjectId || !teamId || selectedTaskIds.size === 0) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const packet = await dailyDispatchService.generateDraft({
        projectId: effectiveProjectId,
        dispatchDate,
        teamId,
        taskIds: [...selectedTaskIds],
        title: `${selectedTeam?.name || 'Crew'} work packet - ${dispatchDate}`,
        notes: notes.trim() || undefined,
        deliveryChannel: channel,
      });
      setActivePacket(packet);
      setNotice(`Dispatch packet created with ${packet.items.length} task(s). Review it, then send.`);
      setDispatches((current) => [packet.dispatch, ...current.filter((row) => row.id !== packet.dispatch.id)]);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to create the dispatch packet.');
    } finally {
      setSaving(false);
    }
  };

  const sendPacket = async (dispatch: DailyDispatch) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const packet = await dailyDispatchService.sendDispatch(dispatch.id, channel);
      setActivePacket(packet);
      setDispatches((current) => current.map((row) => row.id === dispatch.id ? packet.dispatch : row));
      setNotice(`Work packet sent to ${selectedTeam?.name || 'the crew'} with status ${packet.dispatch.status}.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to send the dispatch packet.');
    } finally {
      setSaving(false);
    }
  };

  const sendableDispatch = useMemo(
    () => [activePacket?.dispatch, ...dispatches].find(isDispatchSendable) ?? null,
    [activePacket, dispatches]
  );
  const sendButtonLabel = sendableDispatch && ['failed', 'partially_sent'].includes(sendableDispatch.status)
    ? 'Retry unsent tasks'
    : 'Send to crew';

  if (!effectiveProjectId) {
    return projectsLoading
      ? <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      : <Alert severity="warning">No project is available for dispatch packets.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h4" sx={{ fontSize: 28, fontWeight: 900 }}>Dispatch Packet</Typography>
          <Typography color="text.secondary">{projectName} - issue the selected work plan to a crew</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadDispatchWork} disabled={loading || !teamId}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label="Dispatch date"
              InputLabelProps={{ shrink: true }}
              value={dispatchDate}
              onChange={(event) => setDispatchDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth select label="Crew" value={teamId} onChange={(event) => setTeamId(event.target.value)}>
              {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth select label="Worker delivery" value={channel} onChange={(event) => setChannel(event.target.value as DispatchDeliveryChannel)}>
              <MenuItem value="in_app">In-app only</MenuItem>
              <MenuItem value="whatsapp">External message</MenuItem>
              <MenuItem value="both">In-app and external</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Supervisor instructions" value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Box component="section">
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.25 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Ready tasks</Typography>
            <Typography variant="body2" color="text.secondary">Choose the tasks to include in this crew packet.</Typography>
          </Box>
          <FormControlLabel
            control={<Checkbox checked={allSelected} indeterminate={selectedTaskIds.size > 0 && !allSelected} onChange={() => setSelectedTaskIds(allSelected ? new Set() : new Set(eligibleTasks.map((task) => task.id)))} />}
            label="Select all"
          />
        </Stack>

        <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}><CircularProgress size={20} /><Typography>Loading ready work...</Typography></Stack>
          ) : eligibleTasks.length === 0 ? (
            <Alert severity="info">No ready tasks are eligible for this crew and date.</Alert>
          ) : eligibleTasks.map((task, index) => (
            <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: index === eligibleTasks.length - 1 ? 0 : '1px solid #e3e6e3' }}>
              <Checkbox checked={selectedTaskIds.has(task.id)} onChange={() => toggleTask(task.id)} inputProps={{ 'aria-label': `Include ${task.title}` }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800 }}>{task.title}</Typography>
                <Typography variant="body2" color="text.secondary">{taskLocation(task)}</Typography>
              </Box>
              <Chip size="small" label={task.priority} color={task.priority === 'URGENT' ? 'error' : task.priority === 'HIGH' ? 'warning' : 'default'} />
            </Box>
          ))}
        </Paper>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LocalShipping />}
          onClick={generatePacket}
          disabled={saving || selectedTaskIds.size === 0 || !teamId}
        >
          Create packet ({selectedTaskIds.size})
        </Button>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Send />}
          onClick={() => sendableDispatch && sendPacket(sendableDispatch)}
          disabled={saving || !sendableDispatch}
        >
          {sendButtonLabel}
        </Button>
      </Stack>

      {dispatches.length > 0 && (
        <Box component="section">
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Packets for {dispatchDate}</Typography>
          <Stack spacing={1}>
            {dispatches.map((dispatch) => (
              <Paper key={dispatch.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800 }}>{dispatch.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{dispatch.deliverySummary?.itemCount ?? 0} task(s)</Typography>
                  </Box>
                  <Chip label={dispatch.status} color={dispatch.status === 'sent' ? 'success' : dispatch.status === 'failed' ? 'error' : 'default'} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
